import {
  AimOutlined,
  FolderOpenOutlined,
  MonitorOutlined,
  ReadOutlined,
} from '@ant-design/icons'
import { t } from 'i18next'
import { useMemo } from 'react'
import styled from 'styled-components'
import { useSearchReviews, useUrlQuery, useWorkspaceDetails } from '../hooks'
import { routePaths } from '../pages/RootPage'
import { workspacePanelTypes } from '../pages/WorkspaceDetailsPage/WorkspaceDetailsPage'

const BreadcrumbItem = styled.div`
  color: ${(props) => props.theme.token.colorText};
  display: flex;
`

const MainContainer = styled.div`
  display: flex;
  gap: 8px;
`

const AfterBreadcrumb = () => {
  const {
    basePath,
    selectedRiskReliefDeviationId,
    workspacePanelType,
    selectedConformityCheckDocumentId,
  } = useUrlQuery()
  const { selectedWorkspace } = useWorkspaceDetails({
    preventFetch: true,
  })

  const { searchReviewList } = useSearchReviews({
    preventFetch: true,
  })

  const searchReviewForDeviation = useMemo(
    () =>
      searchReviewList?.find((review) =>
        review.searchQueries?.find(
          (query) => query.id === selectedRiskReliefDeviationId
        )
      ),
    [searchReviewList, selectedRiskReliefDeviationId]
  )

  const documentTitle: string | undefined = useMemo(() => {
    if (!selectedWorkspace?.config?.conformityChecksConfig?.documents) {
      return undefined
    }

    const document =
      selectedWorkspace.config.conformityChecksConfig.documents.find(
        (doc) => doc.id === selectedConformityCheckDocumentId
      )

    return document?.title
  }, [selectedWorkspace, selectedConformityCheckDocumentId])

  const content = useMemo(() => {
    if (basePath === routePaths.WORKSPACE_DETAILS) {
      switch (workspacePanelType) {
        case workspacePanelTypes.SETTINGS:
          return t('Settings')
        case workspacePanelTypes.DEVIATIONS:
          return !!selectedRiskReliefDeviationId
            ? searchReviewForDeviation?.title
            : t('Deviations')
        case workspacePanelTypes.DOCUMENTS:
          return !!selectedConformityCheckDocumentId
            ? documentTitle
            : t('Documents')
        default:
          return t('Overview')
      }
    }
    if (basePath === routePaths.OFR_DOCUMENT_TEMPLATE_DETAILS) {
      return t('Configuration')
    }
  }, [
    basePath,
    searchReviewForDeviation,
    workspacePanelType,
    selectedRiskReliefDeviationId,
    selectedConformityCheckDocumentId,
    documentTitle,
  ])

  const icon = useMemo(() => {
    if (basePath === routePaths.OFR_DOCUMENT_TEMPLATE_DETAILS) {
      return <AimOutlined />
    }
    if (basePath === routePaths.WORKSPACE_DETAILS) {
      if (workspacePanelType === workspacePanelTypes.DEVIATIONS) {
        return !!selectedRiskReliefDeviationId ? (
          <ReadOutlined />
        ) : (
          <MonitorOutlined />
        )
      }
      if (workspacePanelType === workspacePanelTypes.DOCUMENTS) {
        return !!selectedConformityCheckDocumentId ? (
          <FolderOpenOutlined />
        ) : (
          <MonitorOutlined />
        )
      }
    }
  }, [
    basePath,
    workspacePanelType,
    selectedRiskReliefDeviationId,
    selectedConformityCheckDocumentId,
  ])

  return (
    <div>
      {!!content && (
        <MainContainer>
          <BreadcrumbItem>/</BreadcrumbItem>
          <BreadcrumbItem>{!!icon && icon}</BreadcrumbItem>
          <BreadcrumbItem>{content}</BreadcrumbItem>
        </MainContainer>
      )}
    </div>
  )
}

export default AfterBreadcrumb
